import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { useTranslation } from "../hooks/useTranslation";
import {
  billingKeys,
  useBillingAccount,
  useBillingCatalog,
  useBillingProfile,
  useBillingUsage,
  useSaveBillingProfile,
} from "../hooks/useApi";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AppTile, BrandCard, BrandStyles, SPAZI, TINT_COLORS, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { GRIGIO, IntestazioneCasella, Interruttore, SCURO } from "../components/integrations/casella";
import { CAMPO_TESTO, Campo } from "../components/widget/campi";
import { apriEsterno } from "../utils/apriEsterno";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { CONSIGLIATO, GRATUITO, PER_CHI, SU_MISURA, aspettoPiano, selfService } from "../components/billing/piani";
import { PAESI, PAESI_UE, nomePaese, prefissoIva } from "../components/billing/paesi";
import { AloneHover, BORDO_ATTIVO, Etichetta, SchedaPiano } from "../components/billing/SchedaPiano";
import Consumo from "../components/billing/Consumo";
import { trackEvent } from "../lib/analytics";

/**
 * Abbonamento: il piano attuale con i consumi, i piani, i dati di
 * fatturazione e il pagamento.
 *
 * Il piano non cambia mai da qui: lo cambia Stripe (webhook, e la conferma
 * al ritorno dal pagamento, che rilegge da Stripe) oppure un admin di PingUp.
 * Per pagare servono i dati di fatturazione completi, SDI o PEC compresi.
 */

// Le linguette come nella pagina Impostazioni: grigio chiaro, la scelta in bianco.
const SCHEDE = "h-auto rounded-[12px] bg-[#F1F5F9] p-1 text-[#64748B]";
const SCHEDA =
  "h-8 rounded-[9px] px-3 text-sm font-medium text-[#64748B] hover:text-[#020617] focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-[#020617] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.08)]";

const data = (d) =>
  d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";
const numero = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

/** Oltre il limite del piano (0 = illimitato). */
const oltreIlLimite = (usato, limite) => Boolean(limite) && Number(usato || 0) > limite;

/** Lo stato dell'abbonamento in una pillola. */
function StatoAbbonamento({ abbonamento, gratuito }) {
  let pillola = { testo: gratuito ? "Gratuito" : "Attivo", colore: "bg-[#19D163]/10 text-[#0F7A3D]", punto: "bg-[#19D163]" };
  if (abbonamento?.status === "past_due") {
    pillola = { testo: "Rinnovo non riuscito", colore: "bg-red-50 text-red-700", punto: "bg-red-500" };
  } else if (abbonamento?.cancel_at_period_end) {
    pillola = { testo: "Disdetto", colore: "bg-amber-50 text-amber-800", punto: "bg-amber-500" };
  }
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${pillola.colore}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${pillola.punto}`} aria-hidden="true" />
      {pillola.testo}
    </span>
  );
}

const ERRORI = {
  partita_iva_non_valida: "Partita IVA non valida",
  partita_iva_altra_nazione: "È la partita IVA di un'altra nazione: controlla la nazione o il prefisso",
  codice_fiscale_non_valido: "Codice fiscale non valido",
  codice_sdi_non_valido: "Codice SDI di 7 caratteri (0000000 solo insieme alla PEC)",
  pec_non_valida: "Indirizzo PEC non valido",
  email_non_valida: "Indirizzo email non valido",
  telefono_non_valido: "Numero di telefono non valido",
  cap_non_valido: "Il CAP ha 5 cifre",
  provincia_non_valida: "Sigla di 2 lettere, es. MI",
  nazione_non_valida: "Scegli la nazione",
};
const MANCANTE = {
  vat_number: "Serve la partita IVA dell'azienda",
  sdi_code: "Per la fattura elettronica serve il codice SDI o la PEC",
};

// Il profilo salvato ha country "" finche' non si sceglie: il menu mostra
// Italia, quindi Italia deve essere anche il valore che si salva. L'email per
// le fatture, se manca, parte da quella di chi e' collegato: poi la cambia lui.
const modulo = (billing, email) => ({
  ...(billing || {}),
  country: billing?.country || "IT",
  billing_email: billing?.billing_email || email || "",
});

// Un campo mancante smette di essere segnato appena lo si compila. SDI o PEC
// valgono l'uno per l'altro.
const COMPILATO_CON = { sdi_code: ["sdi_code", "pec"] };
const compilato = (form, campo) => (COMPILATO_CON[campo] || [campo]).some((c) => String(form[c] ?? "").trim());

// Iniziato: c'e' almeno un dato oltre alla nazione. Un modulo vuoto non si
// apre gia' pieno di errori: i mancanti si segnano dopo il primo salvataggio.
const iniziato = (billing) => Object.entries(billing || {}).some(([k, v]) => k !== "country" && String(v ?? "").trim());

/**
 * I dati di fatturazione, con i campi mancanti e scritti male segnati.
 * `inModale`: dentro la finestra che si apre prima del primo pagamento (il
 * titolo lo mette la finestra, i bottoni restano in fondo mentre si scorre).
 */
function DatiFatturazione({ profilo, soloLettura, pianoInAttesa, onSalvato, inModale = false, onAnnulla }) {
  const salva = useSaveBillingProfile();
  const { toast } = useToast();
  const { user } = useAuth();
  // In sola lettura niente valore proposto: si vede solo quello salvato.
  const emailProposta = soloLettura ? null : user?.email;
  const [form, setForm] = useState(() => modulo(profilo?.billing, emailProposta));
  const [errori, setErrori] = useState({});
  const [mancanti, setMancanti] = useState(profilo?.mancanti || []);
  const [mostraMancanti, setMostraMancanti] = useState(Boolean(pianoInAttesa) && iniziato(profilo?.billing));
  const italia = (form.country || "IT") === "IT";
  const ue = PAESI_UE.includes(form.country);

  useEffect(() => {
    setForm(modulo(profilo?.billing, emailProposta));
    setMancanti(profilo?.mancanti || []);
  }, [profilo, emailProposta]);
  useEffect(() => {
    if (pianoInAttesa && iniziato(profilo?.billing)) setMostraMancanti(true);
  }, [pianoInAttesa, profilo]);

  const cambia = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const problema = (campo) =>
    ERRORI[errori[campo]] ||
    (mostraMancanti && mancanti.includes(campo) && !compilato(form, campo) ? MANCANTE[campo] || "Obbligatorio" : null);

  const invia = async (e) => {
    e.preventDefault();
    setErrori({});
    try {
      const risposta = await salva.mutateAsync(form);
      setMancanti(risposta.mancanti || []);
      setMostraMancanti(true);
      // Dalla finestra si va al pagamento: la conferma e' quella.
      if (!pianoInAttesa) toast({ title: "Dati di fatturazione salvati" });
      onSalvato?.(risposta);
    } catch (err) {
      // Gli errori di un campo restano sotto il campo; gli altri (server giu',
      // permessi) sono un toast, come nel resto della dashboard.
      const perCampo = err?.data?.errori || {};
      setErrori(perCampo);
      if (!Object.keys(perCampo).length) {
        toast({ title: "Salvataggio non riuscito", description: err?.message || "Riprova fra poco.", variant: "destructive" });
      }
    }
  };

  const campo = (nome, etichetta, { obbligatorio = true, aiuto, tipo = "text", auto, maiuscolo, larghezza } = {}) => {
    const errore = problema(nome);
    return (
      <div className={larghezza}>
        <Campo id={`fatt-${nome}`} etichetta={`${etichetta}${obbligatorio ? " *" : ""}`} aiuto={errore ? null : aiuto}>
          <input
            id={`fatt-${nome}`}
            type={tipo}
            autoComplete={auto}
            value={form[nome] || ""}
            onChange={cambia(nome)}
            disabled={soloLettura}
            aria-invalid={Boolean(errore)}
            aria-describedby={errore ? `fatt-${nome}-errore` : undefined}
            className={`${CAMPO_TESTO} ${maiuscolo ? "uppercase" : ""} ${errore ? "border-red-300 focus:border-red-400" : ""} disabled:bg-[#F8FAFC]`}
          />
        </Campo>
        {errore ? (
          <p id={`fatt-${nome}-errore`} className="mt-1 text-xs text-red-700">
            {errore}
          </p>
        ) : null}
      </div>
    );
  };

  const corpo = (
    <form onSubmit={invia} noValidate className="space-y-6">
      {inModale ? null : (
        <div>
          <h2 className="text-base font-semibold text-[#020617]">Dati di fatturazione</h2>
          <p className="mt-0.5 text-sm text-[#64748B]">
            Servono per la fattura e prima di pagare devono essere completi. Quelli scritti nella pagina di pagamento di Stripe arrivano anche qui.
          </p>
        </div>
      )}

      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Intestazione</legend>
        {campo("company_name", "Ragione sociale", { auto: "organization", larghezza: "sm:col-span-2" })}
        {/* Si vende solo alle aziende: la partita IVA serve sempre. Il codice
            fiscale (solo in Italia) se e' diverso, come in una ditta individuale. */}
        {campo("vat_number", "Partita IVA", {
          aiuto: italia ? "11 cifre" : ue ? `Dell'azienda, con il prefisso ${prefissoIva(form.country)}` : "Il numero fiscale dell'azienda",
          maiuscolo: true,
        })}
        {italia ? campo("tax_code", "Codice fiscale", { obbligatorio: false, aiuto: "Solo se è diverso dalla partita IVA", maiuscolo: true }) : null}
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-6">
        <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Indirizzo</legend>
        {campo("address_line1", "Indirizzo", { auto: "address-line1", larghezza: "sm:col-span-4" })}
        {campo("address_line2", "Civico, interno", { obbligatorio: false, auto: "address-line2", larghezza: "sm:col-span-2" })}
        {campo("city", "Città", { auto: "address-level2", larghezza: "sm:col-span-3" })}
        {campo("postal_code", "CAP", { auto: "postal-code", larghezza: "sm:col-span-1" })}
        {italia ? campo("state", "Provincia", { auto: "address-level1", maiuscolo: true, aiuto: "Es. MI", larghezza: "sm:col-span-2" }) : null}
        <div className={italia ? "sm:col-span-6" : "sm:col-span-2"}>
          <Campo id="fatt-country" etichetta="Nazione *">
            <select
              id="fatt-country"
              value={form.country || "IT"}
              onChange={cambia("country")}
              disabled={soloLettura}
              aria-invalid={Boolean(problema("country"))}
              className={`${CAMPO_TESTO} ${problema("country") ? "border-red-300 focus:border-red-400" : ""} disabled:bg-[#F8FAFC]`}
            >
              {PAESI.map((c) => (
                <option key={c} value={c}>
                  {nomePaese(c)}
                </option>
              ))}
            </select>
          </Campo>
          {problema("country") ? <p className="mt-1 text-xs text-red-700">{problema("country")}</p> : null}
        </div>
      </fieldset>

      {italia ? (
        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Fattura elettronica</legend>
          <p className="text-xs leading-5 text-[#64748B] sm:col-span-2">
            Serve uno dei due. Il codice SDI te lo dà il tuo commercialista o il tuo software di fatturazione.
          </p>
          {campo("sdi_code", "Codice SDI", { obbligatorio: false, maiuscolo: true, aiuto: "7 caratteri" })}
          {campo("pec", "PEC", { obbligatorio: false, tipo: "email" })}
        </fieldset>
      ) : null}

      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Contatti</legend>
        {campo("billing_email", "Email per le fatture", { tipo: "email", auto: "email" })}
        {campo("phone", "Telefono", { obbligatorio: false, tipo: "tel", auto: "tel" })}
        {campo("contact_name", "Referente", { obbligatorio: false, auto: "name", larghezza: "sm:col-span-2" })}
      </fieldset>

      {!soloLettura ? (
        <div
          className={`flex flex-wrap items-center justify-end gap-2 border-t border-[#E2E8F0] pt-4 ${
            // -bottom-6: fino al bordo della finestra, sopra il suo spazio interno.
            inModale ? "sticky -bottom-6 -mx-6 -mb-6 bg-white px-6 pb-6" : ""
          }`}
        >
          {inModale ? (
            <button type="button" className={GRIGIO} onClick={onAnnulla}>
              Annulla
            </button>
          ) : null}
          <button type="submit" className={SCURO} disabled={salva.isPending}>
            {salva.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            {pianoInAttesa ? `Salva e passa a ${pianoInAttesa.name}` : "Salva i dati"}
            {pianoInAttesa ? <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          </button>
        </div>
      ) : null}
    </form>
  );

  return inModale ? corpo : <BrandCard className="p-5 sm:p-6">{corpo}</BrandCard>;
}

// Le risposte dell'API in parole.
function messaggioErrore(err) {
  switch (err?.code) {
    case "stripe_non_configurato":
      return "I pagamenti online non sono ancora attivi. Scrivi a PingUp per attivare il piano.";
    case "abbonamento_gia_attivo":
      return "Hai già un abbonamento: per cambiare piano usa «Gestisci abbonamento».";
    case "abbonamento_stripe_attivo":
      return "Disdici prima l'abbonamento Stripe: poi puoi attivare un piano a mano.";
    case "piano_senza_prezzo":
      return "Questo piano non si può ancora acquistare online.";
    case "piano_non_acquistabile":
      return "Questo piano non si acquista online: scrivi a PingUp.";
    case "permesso_fatturazione_mancante":
      return "Solo chi gestisce la fatturazione dell'organizzazione può cambiare piano.";
    case "nessun_cliente_stripe":
      return "Non c'è ancora un abbonamento a pagamento da gestire.";
    case "cambio_programmato":
      return "Hai già un cambio di piano programmato per il rinnovo: annullalo prima di sceglierne un altro.";
    case "nessun_cambio_programmato":
      return "Non c'è nessun cambio di piano programmato.";
    case "stesso_piano":
      return "È già il tuo piano.";
    default:
      return err?.message || "Qualcosa non è andato. Riprova fra poco.";
  }
}

const Billing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { canManageBilling, isMasterOrg } = useTenant();
  const { toast } = useToast();
  const onboarding = typeof sessionStorage !== "undefined" && sessionStorage.getItem("pingup-onboarding-mode");

  const catalogo = useBillingCatalog();
  const consumi = useBillingUsage();
  const conto = useBillingAccount({ enabled: canManageBilling });
  const profilo = useBillingProfile({ enabled: canManageBilling });

  const [inCorso, setInCorso] = useState(null);
  // Pagamento fatto, conferma della banca in arrivo: si rilegge l'account.
  const [inAttivazione, setInAttivazione] = useState(false);
  const [pianoInAttesa, setPianoInAttesa] = useState(null);
  // Annuale o mensile: la scelta compare solo se qualche piano ha il prezzo mensile.
  const [periodo, setPeriodo] = useState("yearly");
  // Con un piano attivo la pagina ha due schede: piano e consumi, dati di fatturazione.
  const [scheda, setScheda] = useState("piano");
  // Senza dati di fatturazione completi, "Passa a" apre la finestra con il modulo.
  const [modaleAperta, setModaleAperta] = useState(false);
  const confermato = useRef(false);
  const acquistoTracciato = useRef(false);
  const tracciaAcquisto = () => {
    if (acquistoTracciato.current) return;
    acquistoTracciato.current = true;
    trackEvent("bot_ai_purchase");
  };

  const pagamenti = conto.data?.pagamenti || {};
  const abbonamento = conto.data?.subscription || consumi.data?.subscription || null;
  const pianoAttuale = conto.data?.subscription?.plan?.key || consumi.data?.plan?.key || GRATUITO;
  const conStripe = abbonamento?.billing_mode === "stripe";
  const credito = Number(conto.data?.subscription?.credito_eur) || 0;
  // Piano piu' economico programmato per il rinnovo: fino ad allora vale quello pagato.
  const programmato = conto.data?.subscription?.cambio_programmato || null;
  const puoGestire = canManageBilling && pagamenti.puo_gestire !== false;
  // Venduto offline: il piano lo sceglie e lo applica PingUp, niente acquisti
  // online (resta l'attivazione manuale per gli admin).
  const offline = Boolean(pagamenti.vendita_offline);
  // Le schede servono solo con un piano attivo e con i dati di fatturazione da
  // mostrare. Sul gratuito il modulo non sta nella pagina: si apre in una
  // finestra quando si sceglie un piano.
  const conSchede = pianoAttuale !== GRATUITO && canManageBilling && !offline;

  const piani = useMemo(() => {
    const elenco = [...(catalogo.data?.plans || [])];
    // Gratuito per primo, su misura per ultimo, gli altri per prezzo.
    const ordine = (p) => (p.key === GRATUITO ? -1 : p.key === SU_MISURA ? 1e9 : p.price_yearly || p.price_monthly * 12 || 0);
    return elenco.sort((a, b) => ordine(a) - ordine(b));
  }, [catalogo.data]);
  // In griglia solo i piani che si comprano da soli. Gli altri (plan_custom,
  // plan_custom_400...) si vendono fuori: una fascia sotto, con il piano
  // attuale se e' uno di loro.
  const inGriglia = piani.filter((p) => selfService(p.key));
  const suMisura = (!selfService(pianoAttuale) && piani.find((p) => p.key === pianoAttuale)) || piani.find((p) => p.key === SU_MISURA) || null;
  const conMensile = inGriglia.some((p) => p.key !== GRATUITO && p.price_monthly > 0 && p.stripe_price_id_monthly);
  const intervallo = conMensile ? periodo : "yearly";
  // In evidenza solo se sta sopra il piano di adesso.
  const posizione = (chiave) => inGriglia.findIndex((p) => p.key === chiave);
  const evidenza = !offline && selfService(pianoAttuale) && posizione(CONSIGLIATO) > posizione(pianoAttuale) ? CONSIGLIATO : null;
  // Il nome mandato dal server vince: al ritorno da Stripe il catalogo puo'
  // non essere ancora arrivato.
  const nomePiano = (chiave, nome) =>
    nome || piani.find((p) => p.key === chiave)?.name || String(chiave || "").replace(/^plan_/, "").toUpperCase();

  const aggiorna = () => {
    queryClient.invalidateQueries({ queryKey: billingKeys.account });
    queryClient.invalidateQueries({ queryKey: billingKeys.usage });
  };

  // Tutto quello che succede lo dice il toast in basso a destra, come nel resto
  // della dashboard (niente avvisi in pagina). Dopo un pagamento partito
  // dall'onboarding il toast ha il bottone per tornare a creare l'assistente,
  // e resta un po' di piu'.
  const conferma = (titolo, testo) => {
    toast(
      onboarding
        ? {
            title: titolo,
            description: testo,
            duration: 15000,
            action: (
              <ToastAction altText="Continua a creare il tuo assistente" onClick={() => navigate("/bots/new")}>
                Continua a creare il tuo assistente
              </ToastAction>
            ),
          }
        : { title: titolo, description: testo }
    );
  };
  const segnalaErrore = (titolo, err) => toast({ title: titolo, description: messaggioErrore(err), variant: "destructive" });

  // Ritorno da Stripe: ?pagamento=ok&sessione=cs_... oppure ?pagamento=annullato,
  // e ?portale=ok dal portale. Si chiede al server di rileggere da Stripe (la
  // stessa cosa che fa il webhook); se la banca non ha ancora confermato si
  // aspetta il webhook.
  useEffect(() => {
    const parametri = new URLSearchParams(location.search);
    const stato = parametri.get("pagamento");
    const sessione = parametri.get("sessione");
    const dalPortale = parametri.get("portale") === "ok";
    if ((!stato && !dalPortale) || confermato.current) return;
    confermato.current = true;
    navigate(location.pathname, { replace: true });

    if (dalPortale) {
      // Cambio di piano, disdetta o carta nuova: il piano giusto subito, senza
      // aspettare il webhook. Il toast solo se e' cambiato qualcosa.
      api.billing
        .sync()
        .then((r) => {
          aggiorna();
          const cambiati = r?.cambiati || [];
          if (cambiati.includes("plan_id") && r.piano !== GRATUITO) {
            tracciaAcquisto();
          }
          if (cambiati.includes("scheduled_plan_id") && r?.programmato) {
            conferma(`Dal ${data(r.programmato_dal)} passerai a ${nomePiano(r.programmato, r.nome_programmato)}`, "Fino ad allora tieni il piano che hai già pagato.");
          } else if (r?.azione === "tornato_gratuito" && cambiati.includes("plan_id")) {
            conferma("Abbonamento chiuso", "Sei tornato al piano gratuito.");
          } else if (cambiati.includes("plan_id")) {
            conferma(`Ora hai il piano ${nomePiano(r.piano, r.nome_piano)}`, "Il cambio vale da subito.");
          } else if (cambiati.includes("cancel_at_period_end")) {
            conferma("Abbonamento aggiornato", "Trovi qui sotto fino a quando resta attivo.");
          }
        })
        .catch(() => {
          aggiorna();
          toast({ title: "Non siamo riusciti a rileggere l'abbonamento", description: "Se non vedi le modifiche, ricarica la pagina fra qualche minuto." });
        });
      return;
    }

    if (stato === "annullato") {
      conferma("Pagamento annullato", "Non è stato addebitato niente.");
      return;
    }
    if (stato === "ok" && sessione) {
      toast({ title: "Stiamo attivando il piano…", description: "Ci vuole qualche secondo." });
      api.billing
        .confirmCheckout(sessione)
        .then((r) => {
          aggiorna();
          if (r?.azione === "attivato") {
            tracciaAcquisto();
            conferma(`Pagamento riuscito: il piano ${nomePiano(r.piano, r.nome_piano)} è attivo`, "Trovi la ricevuta nell'email per le fatture.");
          } else {
            setInAttivazione(true);
            toast({ title: "Pagamento ricevuto, in attesa della conferma della banca", description: "Il piano si attiva da solo appena arriva: puoi chiudere questa pagina." });
          }
        })
        .catch(() => {
          aggiorna();
          setInAttivazione(true);
          toast({ title: "Pagamento ricevuto", description: "Il piano si attiva da solo entro qualche minuto." });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // In attesa della conferma della banca: si rilegge l'account ogni pochi
  // secondi, per un po'; quando il piano arriva lo dice il toast.
  useEffect(() => {
    if (!inAttivazione) return undefined;
    let giri = 0;
    const timer = setInterval(() => {
      giri += 1;
      aggiorna();
      if (giri >= 10) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inAttivazione]);
  useEffect(() => {
    if (inAttivazione && conStripe && pianoAttuale !== GRATUITO && !conto.isFetching) {
      setInAttivazione(false);
      tracciaAcquisto();
      conferma(`Il piano ${nomePiano(pianoAttuale, conto.data?.subscription?.plan?.name)} è attivo`, "Trovi la ricevuta nell'email per le fatture.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inAttivazione, conStripe, pianoAttuale, conto.isFetching]);

  // La finestra con i dati di fatturazione, per il piano che si vuole comprare.
  const chiediDati = (chiave) => {
    setPianoInAttesa(chiave);
    setModaleAperta(true);
  };

  // Dall'onboarding o dal limite raggiunto ("Passa a PLUS", ?piano=): si apre
  // la finestra dei dati, ma solo a chi puo' davvero comprare, e solo quando
  // il conto e' arrivato (prima non si sa).
  const pianoRichiesto = useRef(new URLSearchParams(location.search).get("piano"));
  useEffect(() => {
    const chiave = pianoRichiesto.current;
    if (!chiave || chiave === GRATUITO || (canManageBilling && !conto.data)) return;
    pianoRichiesto.current = null;
    if (puoGestire && !offline && pagamenti.stripe_attivo) chiediDati(chiave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageBilling, conto.data, puoGestire, offline]);
  const chiudiModale = () => {
    setModaleAperta(false);
    setPianoInAttesa(null);
  };

  // Prima di pagare si passa sempre dalla finestra dei dati di fatturazione:
  // se sono completi basta controllarli (finiscono in fattura). `confermati`:
  // appena salvati dalla finestra, si va al pagamento.
  const paga = async (chiave, confermati = false) => {
    if (!confermati) {
      chiediDati(chiave);
      return;
    }
    setInCorso(chiave);
    try {
      const { url } = await api.billing.checkout(chiave, intervallo);
      apriEsterno(url);
    } catch (err) {
      setInCorso(null);
      if (err?.code === "dati_fatturazione_incompleti") {
        queryClient.invalidateQueries({ queryKey: billingKeys.profile });
        chiediDati(chiave);
      } else {
        segnalaErrore("Pagamento non avviato", err);
      }
    }
  };

  // Senza opzioni il portale generico; { plan_key }: la conferma del passaggio
  // a quel piano, con l'importo di oggi calcolato da Stripe; { disdici }.
  const apriPortale = async (opzioni = {}) => {
    setInCorso(opzioni.plan_key || (opzioni.disdici ? "disdetta" : "portale"));
    try {
      const { url } = await api.billing.portal(opzioni);
      apriEsterno(url);
    } catch (err) {
      setInCorso(null);
      segnalaErrore("Portale non disponibile", err);
    }
  };

  // Il cambio verso un piano piu' economico, programmato per il rinnovo, si annulla.
  const annullaCambio = async () => {
    setInCorso("annulla-cambio");
    try {
      await api.billing.cancelScheduledChange();
      aggiorna();
      conferma("Cambio annullato", `Al rinnovo resti su ${nomePiano(pianoAttuale)}.`);
    } catch (err) {
      segnalaErrore("Cambio non annullato", err);
    } finally {
      setInCorso(null);
    }
  };

  const attivaManuale = async (chiave) => {
    setInCorso(`manuale-${chiave}`);
    try {
      await api.billing.updateSubscription({ plan_key: chiave, mode: "manual" });
      aggiorna();
      conferma(`Piano ${nomePiano(chiave)} attivato a mano`, "Senza pagamento: solo per clienti che pagano a parte.");
    } catch (err) {
      segnalaErrore("Attivazione non riuscita", err);
    } finally {
      setInCorso(null);
    }
  };

  const azionePiano = (piano) => {
    const attuale = piano.key === pianoAttuale;
    if (attuale) return <span className={`${GRIGIO} w-full cursor-default bg-[#F8FAFC] text-[#64748B] hover:bg-[#F8FAFC]`}>Piano attuale</span>;
    if (piano.key === SU_MISURA) {
      return <span className="block w-full text-center text-xs leading-5 text-[#64748B]">Su misura: scrivi a PingUp</span>;
    }
    if (offline) {
      return <span className="block w-full text-center text-xs leading-5 text-[#64748B]">Lo attiva PingUp</span>;
    }
    if (!puoGestire) return null;
    // Con un cambio programmato si vede quando parte; gli altri cambi aspettano che lo si annulli.
    if (conStripe && programmato) {
      return piano.key === programmato.piano?.key ? (
        <span className="block w-full text-center text-xs leading-5 text-[#64748B]">Dal {data(programmato.dal)}</span>
      ) : (
        <button type="button" disabled className={`${GRIGIO} w-full`} title="Annulla prima il cambio programmato">
          {piano.key === GRATUITO ? "Disdici l'abbonamento" : `Passa a ${piano.name}`}
        </button>
      );
    }
    if (piano.key === GRATUITO) {
      return conStripe ? (
        <button type="button" onClick={() => apriPortale({ disdici: true })} disabled={Boolean(inCorso)} className={`${GRIGIO} w-full`}>
          {inCorso === "disdetta" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          Disdici l'abbonamento
        </button>
      ) : null;
    }
    if (conStripe) {
      // Si apre la conferma di Stripe. Verso un piano piu' caro: la differenza
      // si paga subito. Verso uno piu' economico: parte al rinnovo, senza crediti.
      const alRinnovo = posizione(piano.key) < posizione(pianoAttuale);
      return (
        <button type="button" onClick={() => apriPortale({ plan_key: piano.key, interval: intervallo })} disabled={Boolean(inCorso)} className={`${GRIGIO} w-full`}>
          {inCorso === piano.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {alRinnovo ? `Passa a ${piano.name} al rinnovo` : `Passa a ${piano.name}`}
        </button>
      );
    }
    const prezzoStripe = intervallo === "monthly" ? piano.stripe_price_id_monthly : piano.stripe_price_id_yearly;
    const acquistabile = Boolean(prezzoStripe) && pagamenti.stripe_attivo;
    // Un solo bottone nero per zona: quello del piano in evidenza.
    const stile = evidenza && piano.key !== evidenza ? GRIGIO : SCURO;
    return (
      <button type="button" onClick={() => paga(piano.key)} disabled={!acquistabile || Boolean(inCorso)} className={`${stile} w-full`} title={acquistabile ? undefined : "Pagamenti online non ancora attivi"}>
        {inCorso === piano.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
        Passa a {piano.name}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  };

  const usi = consumi.data?.usage || {};
  const limiti = consumi.data?.effective?.limits || {};
  const oltreIlPiano =
    oltreIlLimite(usi.sources_count, limiti.sources_max) ||
    oltreIlLimite(usi.widgets_count, limiti.widgets_max) ||
    oltreIlLimite(usi.domains_count, limiti.domains_max);
  // Sotto il nome del piano, nella sezione «Il tuo piano».
  const rinnovo = programmato
    ? `Dal ${data(programmato.dal)} passerai a ${programmato.piano?.name || nomePiano(programmato.piano?.key)}`
    : abbonamento?.cancel_at_period_end && abbonamento.current_period_end
      ? `Attivo fino al ${data(abbonamento.current_period_end)}, poi torna gratuito`
      : conStripe && abbonamento?.current_period_end && abbonamento.status !== "past_due"
        ? `Si rinnova il ${data(abbonamento.current_period_end)}`
        : null;
  const pianoInAttesaDati = piani.find((p) => p.key === pianoInAttesa) || null;

  const dettaglioPiano = piani.find((p) => p.key === pianoAttuale);
  const prezzoAttuale = dettaglioPiano?.price_yearly > 0 ? `${numero(dettaglioPiano.price_yearly)} € all'anno + IVA` : null;
  // Gli stati che prima erano avvisi in pagina stanno sotto il nome del piano.
  const rinnovoFallito = abbonamento?.status === "past_due" && puoGestire;
  const notaPiano = !canManageBilling
    ? [
        isMasterOrg
          ? "Lo gestisce il titolare dell'organizzazione: da qui vedi il piano e i consumi."
          : "Si gestisce dall'organizzazione principale: da qui vedi il piano e i consumi.",
        // Chi non puo' pagare deve almeno sapere che il rinnovo e' fermo.
        abbonamento?.status === "past_due" ? "Il rinnovo non è andato a buon fine: avvisa il titolare." : null,
      ]
        .filter(Boolean)
        .join(" ")
    : offline
      ? "Il tuo piano lo gestisce PingUp: per cambiarlo scrivi al tuo referente."
      : rinnovoFallito
        ? "Il rinnovo non è andato a buon fine: aggiorna la carta. Il piano resta attivo mentre ritentiamo l'addebito."
        : [
            pianoAttuale === GRATUITO ? "Gratis, senza carta" : prezzoAttuale,
            rinnovo || (!conStripe && pianoAttuale !== GRATUITO ? "Attivato da PingUp" : null),
            // Dopo un piano piu' economico la differenza resta su Stripe e paga i rinnovi.
            credito > 0 ? `Credito di ${numero(credito)} € per i prossimi rinnovi` : null,
          ]
            .filter(Boolean)
            .join(" · ");

  // La pagina in due pezzi: con un piano attivo diventano due schede.
  const sezionePiano = (
    <>
      {/* Il tuo piano: nome, stato, prezzo e rinnovo, con la tessera del suo
          colore (come nelle schede dei piani) e la gestione su Stripe. */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[#020617]">Il tuo piano</h2>
          <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">Vale per l'organizzazione e per tutti i suoi assistenti.</p>
        </div>
        {consumi.isLoading && !conto.data ? (
          <Skeleton className="h-[84px] rounded-[14px]" />
        ) : (
          <BrandCard className="flex flex-wrap items-center gap-4 p-5">
            <AppTile app={aspettoPiano(pianoAttuale)} />
            <div className="min-w-[12rem] flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="text-base font-semibold text-[#020617]">{nomePiano(pianoAttuale)}</p>
                <StatoAbbonamento abbonamento={abbonamento} gratuito={pianoAttuale === GRATUITO} />
              </div>
              <p className={`mt-0.5 text-sm ${rinnovoFallito ? "text-red-700" : "text-[#64748B]"}`}>{notaPiano}</p>
            </div>
            {rinnovoFallito ? (
              <button type="button" onClick={() => apriPortale()} disabled={Boolean(inCorso)} className={`${SCURO} shrink-0`}>
                {inCorso === "portale" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                Aggiorna la carta
              </button>
            ) : puoGestire && pagamenti.ha_cliente_stripe ? (
              <span className="flex shrink-0 flex-wrap gap-2">
                {programmato ? (
                  <button type="button" onClick={annullaCambio} disabled={Boolean(inCorso)} className={GRIGIO}>
                    {inCorso === "annulla-cambio" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                    Annulla il cambio
                  </button>
                ) : null}
                <button type="button" onClick={() => apriPortale()} disabled={Boolean(inCorso)} className={GRIGIO}>
                  {inCorso === "portale" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />}
                  Gestisci abbonamento
                </button>
              </span>
            ) : null}
          </BrandCard>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-[#020617]">Consumi</h2>
          <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">
            {oltreIlPiano
              ? `Stai usando più di quanto comprende ${nomePiano(pianoAttuale)}: per aggiungere altro serve un piano più grande.`
              : `Quanto stai usando di quello che comprende ${nomePiano(pianoAttuale)}.`}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {consumi.isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[130px] rounded-[14px]" />)
          ) : (
            <>
              <Consumo etichetta="Risposte questo mese" usato={usi.messages_used} limite={limiti.messages_per_month} ripartono={usi.messages_period_end} />
              <Consumo etichetta="Fonti" usato={usi.sources_count} limite={limiti.sources_max} />
              <Consumo etichetta="Siti o assistenti" usato={usi.widgets_count} limite={limiti.widgets_max} />
              <Consumo etichetta="Domini" usato={usi.domains_count} limite={limiti.domains_max} />
            </>
          )}
        </div>
      </section>

      {/* I piani */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#020617]">Piani</h2>
            <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">
              {intervallo === "monthly" ? "Prezzi mensili" : "Prezzi annuali"}, IVA esclusa.
              {/* Come si paga lo si dice solo a chi puo' comprare online. */}
              {puoGestire && !offline ? " Si paga con carta e l'abbonamento si rinnova da solo; puoi disdirlo quando vuoi." : null}
            </p>
          </div>
          {conMensile ? (
            <Interruttore
              etichetta="Come pagare"
              valore={periodo}
              onCambia={setPeriodo}
              voci={[
                { valore: "monthly", etichetta: "Mensile" },
                { valore: "yearly", etichetta: "Annuale" },
              ]}
            />
          ) : null}
        </div>

        {/* pt-3: lo spazio per le etichette sul bordo alto. */}
        <div className="grid gap-4 pt-3 sm:grid-cols-2 xl:grid-cols-4">
          {catalogo.isLoading
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-[14px]" />)
            : inGriglia.map((piano) => (
                <SchedaPiano
                  key={piano.key}
                  piano={piano}
                  intervallo={intervallo}
                  attuale={piano.key === pianoAttuale}
                  inEvidenza={piano.key === evidenza}
                  azione={azionePiano(piano)}
                />
              ))}
        </div>

        {suMisura ? (
          <BrandCard
            className={`group relative isolate flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6 ${
              suMisura.key === pianoAttuale ? BORDO_ATTIVO : ""
            }`}
          >
            <AloneHover colore={TINT_COLORS[aspettoPiano(suMisura.key).tint]} />
            <AppTile app={aspettoPiano(suMisura.key)} />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-[#020617]">{suMisura.name}</h3>
                {suMisura.key === pianoAttuale ? <Etichetta tipo="attivo" inLinea /> : null}
              </div>
              <p className="text-sm leading-6 text-[#475569]">
                {suMisura.key === SU_MISURA
                  ? `${PER_CHI[SU_MISURA]} Risposte, fonti e siti senza limiti fissi, con l'assistenza di PingUp.`
                  : "Piano concordato con PingUp: limiti e prezzo decisi insieme. Per cambiarlo scrivi a PingUp."}
              </p>
            </div>
            {suMisura.key === pianoAttuale ? null : <span className="shrink-0 text-sm font-medium text-[#020617]">Scrivi a PingUp</span>}
          </BrandCard>
        ) : null}

        {/* Solo per gli admin di PingUp, fuori dalle schede: i clienti non la vedono. */}
        {pagamenti.admin ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[10px] border border-dashed border-[#CBD5E1] px-4 py-2.5 text-xs text-[#64748B]">
            <span className="font-medium text-[#334155]">Admin PingUp · attiva senza pagamento:</span>
            {[...inGriglia, ...(suMisura ? [suMisura] : [])]
              .filter((p) => p.key !== pianoAttuale)
              .map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => attivaManuale(p.key)}
                  disabled={Boolean(inCorso)}
                  aria-label={`Attiva senza pagamento ${p.name}`}
                  className="font-medium text-[#18181B] underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {inCorso === `manuale-${p.key}` ? "Attivazione…" : p.name}
                </button>
              ))}
          </div>
        ) : null}
        {canManageBilling && conto.data && !pagamenti.stripe_attivo ? (
          <p className="text-xs text-[#64748B]">I pagamenti online non sono ancora attivi: per un piano a pagamento scrivi a PingUp.</p>
        ) : null}
      </section>
    </>
  );
  // I dati di fatturazione, nella seconda scheda (con un piano attivo).
  const sezioneFatturazione = profilo.isLoading ? (
    <Skeleton className="h-96 rounded-[14px]" />
  ) : (
    <DatiFatturazione profilo={profilo.data} soloLettura={!puoGestire} />
  );

  // Prima del pagamento, se mancano i dati: la finestra con il modulo, poi Stripe.
  const modale = (
    <Dialog open={modaleAperta} onOpenChange={(aperta) => !aperta && chiudiModale()}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-[14px] p-6"
        onOpenAutoFocus={(e) => {
          // Il focus sul primo campo da sistemare, senza selezionarne il testo
          // (Radix lo selezionerebbe tutto).
          e.preventDefault();
          const finestra = e.currentTarget;
          requestAnimationFrame(() => (finestra?.querySelector('[aria-invalid="true"]') || finestra)?.focus());
        }}
      >
        <DialogHeader>
          <DialogTitle>Dati di fatturazione</DialogTitle>
          <DialogDescription>
            {pianoInAttesaDati
              ? profilo.data?.completo
                ? `Controlla i dati per la fattura, poi si apre il pagamento con carta per ${pianoInAttesaDati.name}.`
                : `Per passare a ${pianoInAttesaDati.name} servono i dati per la fattura. Poi si apre il pagamento con carta.`
              : "Servono per la fattura."}{" "}
            I prezzi sono IVA esclusa: al pagamento si aggiunge l'IVA della nazione di fatturazione (22% in Italia).
          </DialogDescription>
        </DialogHeader>
        {profilo.isLoading ? (
          <Skeleton className="h-96 rounded-[14px]" />
        ) : (
          <DatiFatturazione
            inModale
            profilo={profilo.data}
            soloLettura={!puoGestire}
            pianoInAttesa={pianoInAttesaDati}
            onAnnulla={chiudiModale}
            onSalvato={(r) => {
              if (!pianoInAttesa || !r.completo) return;
              setModaleAperta(false);
              paga(pianoInAttesa, true);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />
      {/* Il piano, lo stato e il rinnovo stanno nella sezione «Il tuo piano». */}
      <IntestazioneCasella icona={CreditCard} titolo={t("billing.title", "Abbonamento")} />

      {/* Niente avvisi in pagina: gli eventi (pagamento, errori) sono toast,
          gli stati (gestito da PingUp, rinnovo non riuscito) stanno nella
          scheda «Il tuo piano». */}
      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-6 pb-10">
        {conSchede ? (
          <Tabs value={scheda} onValueChange={setScheda}>
            <TabsList className={SCHEDE}>
              <TabsTrigger value="piano" className={SCHEDA}>
                Piano e consumi
              </TabsTrigger>
              <TabsTrigger value="fatturazione" className={SCHEDA}>
                Dati di fatturazione
              </TabsTrigger>
            </TabsList>
            <TabsContent value="piano" className={`${SPAZI.schedeContenuto} space-y-6`}>
              {sezionePiano}
            </TabsContent>
            <TabsContent value="fatturazione" className={SPAZI.schedeContenuto}>
              {sezioneFatturazione}
            </TabsContent>
          </Tabs>
        ) : (
          sezionePiano
        )}
      </div>
      {modale}
    </div>
  );
};

export default Billing;
